// OnboardingBanner - Dismissible banner encouraging place suggestions

import React, { useState, useEffect } from 'react';
import { X, MapPin, Briefcase, ArrowRight } from 'lucide-react';
import { useRealm } from '../../context/RealmContext';

const STORAGE_KEY = 'goodplace-banner-dismissed';

interface OnboardingBannerProps {
  onSuggestPlace: () => void;
}

export function OnboardingBanner({ onSuggestPlace }: OnboardingBannerProps) {
  const { isGoodWork } = useRealm();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const isDismissed = localStorage.getItem(STORAGE_KEY);
    if (!isDismissed) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    setIsClosing(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, 'true');
      setIsVisible(false);
    }, 300);
  };

  const handleSuggest = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    onSuggestPlace();
  };

  if (!isVisible) return null;

  return (
    <div
      className={`mx-4 sm:mx-6 lg:mx-8 mb-6 transition-all duration-300 ${
        isClosing ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'
      }`}
    >
      <div className="relative bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10 border border-indigo-500/20 rounded-xl p-4 sm:p-5">
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-black/5 transition"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>

        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Icon */}
          <div className="p-3 bg-white rounded-xl shadow-sm">
            {isGoodWork ? (
              <Briefcase className="w-6 h-6 text-indigo-600" />
            ) : (
              <MapPin className="w-6 h-6 text-indigo-600" />
            )}
          </div>

          {/* Text */}
          <div className="flex-1 pr-8 sm:pr-0">
            <h3 className="font-semibold text-gray-900 mb-1">
              {isGoodWork
                ? 'Kennst Du einen GoodWork Place?'
                : 'Kennst du einen guten Ort?'}
            </h3>
            <p className="text-sm text-gray-600">
              {isGoodWork
                ? 'Teile deinen Lieblingsarbeitsplatz mit der Community. Gemeinsam finden wir die besten Orte zum Arbeiten!'
                : 'Teile dein Lieblingscafe, deinen Lieblingspark oder einen besonderen Ort mit der Community. Gemeinsam entdecken wir die besten Plaetze!'}
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleSuggest}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-500 text-white font-medium rounded-lg hover:bg-indigo-600 transition whitespace-nowrap"
          >
            <span>Ort vorschlagen</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingBanner;
